import React, { useState } from 'react';
import { Card, CardSkeleton, Button } from '@/components/ui';
import type { FeedbackListResponse, WorkflowStep } from '@/types/feedback';

interface RecentFeedbackTableProps {
  feedbackList: FeedbackListResponse | null;
  loading: boolean;
  onFilterChange: (options: { step?: string; rating?: string }) => void;
}

const STEP_LABELS: Record<string, string> = {
  discovery: 'Discovery',
  design: 'Design',
  multiagent: 'Architecture',
  technical: 'Technical',
  codegen: 'Code Gen',
  operations: 'Operations',
};

const STEP_OPTIONS: WorkflowStep[] = [
  'discovery',
  'design',
  'multiagent',
  'technical',
  'codegen',
  'operations',
];

export const RecentFeedbackTable: React.FC<RecentFeedbackTableProps> = ({
  feedbackList,
  loading,
  onFilterChange,
}) => {
  const [stepFilter, setStepFilter] = useState<string>('');
  const [ratingFilter, setRatingFilter] = useState<string>('');

  const handleStepChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const step = e.target.value;
    setStepFilter(step);
    onFilterChange({
      step: step || undefined,
      rating: ratingFilter || undefined,
    });
  };

  const handleRatingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const rating = e.target.value;
    setRatingFilter(rating);
    onFilterChange({
      step: stepFilter || undefined,
      rating: rating || undefined,
    });
  };

  const handleClearFilters = () => {
    setStepFilter('');
    setRatingFilter('');
    onFilterChange({});
  };

  if (loading) {
    return <CardSkeleton />;
  }

  const items = feedbackList?.items || [];
  const hasFilters = stepFilter || ratingFilter;

  return (
    <Card padding="lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-secondary-900">
          Recent Feedback
        </h3>

        <div className="flex items-center gap-3">
          <select
            value={stepFilter}
            onChange={handleStepChange}
            className="px-3 py-1.5 text-sm border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Steps</option>
            {STEP_OPTIONS.map((step) => (
              <option key={step} value={step}>
                {STEP_LABELS[step]}
              </option>
            ))}
          </select>

          <select
            value={ratingFilter}
            onChange={handleRatingChange}
            className="px-3 py-1.5 text-sm border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Ratings</option>
            <option value="positive">Positive</option>
            <option value="negative">Negative</option>
          </select>

          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={handleClearFilters}>
              Clear
            </Button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-secondary-500 py-8 text-center">
          {hasFilters
            ? 'No feedback matches the selected filters'
            : 'No feedback submitted yet'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Step
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Comment
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  User
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-200">
              {items.map((item) => {
                const date = new Date(item.created_at);
                const formattedDate = date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                });

                return (
                  <tr key={item.id} className="hover:bg-secondary-50">
                    <td className="px-4 py-3 text-sm text-secondary-600 whitespace-nowrap">
                      {formattedDate}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-secondary-900">
                      {STEP_LABELS[item.step] || item.step}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.rating === 'positive' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <svg
                            className="w-3 h-3 mr-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                          </svg>
                          Good
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <svg
                            className="w-3 h-3 mr-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.105-1.79l-.05-.025A4 4 0 0011.055 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
                          </svg>
                          Poor
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-600 max-w-xs truncate">
                      {item.comment || (
                        <span className="text-secondary-400 italic">
                          No comment
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-600 truncate max-w-[150px]">
                      {item.user_email || 'Anonymous'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {feedbackList && feedbackList.total > items.length && (
        <div className="mt-4 pt-4 border-t border-secondary-200 text-center">
          <p className="text-sm text-secondary-500">
            Showing {items.length} of {feedbackList.total} feedback entries
          </p>
        </div>
      )}
    </Card>
  );
};

export default RecentFeedbackTable;
